// src/features/records/components/shared/AvatarStack.js
import React from 'react';
import { Avatar } from '../../../../components/avatars';
import styles from './AvatarStack.module.css';

const VALID_SIZES = ['sm', 'md', 'lg'];

/**
 * Avatar stack component for displaying overlapped avatars
 * @param {Object} props - Component props
 * @param {Array<{id: string, name: string, avatarUrl?: string}>} props.users - Users to display
 * @param {number} [props.max=3] - Maximum avatars to show before +N
 * @param {string} [props.size='md'] - Size variant (sm, md, lg)
 * @param {string} [props.className] - Additional CSS class
 */
function AvatarStack({ users = [], max = 3, size = 'md', className = '' }) {
  const displayUsers = users.slice(0, max);
  const remainingCount = Math.max(0, users.length - max);
  const resolvedSize = VALID_SIZES.includes(size) ? size : 'md';
  
  if (users.length === 0) {
    return null;
  }
  
  const sizeClass = styles[`size-${resolvedSize}`] || '';

  return (
    <div 
      className={`${styles.avatarStack} ${sizeClass} ${className}`}
      aria-label={`${users.length} user${users.length !== 1 ? 's' : ''} selected`}
    >
      {displayUsers.map((user, index) => (
        <div
          key={user.id}
          className={styles.avatarWrapper}
          style={{ 
            zIndex: max - index,
            marginLeft: index === 0 ? 0 : `-${resolvedSize === 'sm' ? 8 : resolvedSize === 'lg' ? 12 : 10}px`
          }}
        >
          <Avatar
            name={user.name}
            src={user.avatarUrl}
            size={resolvedSize}
            className={`${styles.avatar} ${styles.stackedAvatar}`}
            alt={`${user.name} avatar`}
          />
        </div>
      ))}
      
      {remainingCount > 0 && (
        <div
          className={styles.avatarWrapper}
          style={{ 
            zIndex: 0,
            marginLeft: `-${resolvedSize === 'sm' ? 8 : resolvedSize === 'lg' ? 12 : 10}px`
          }}
        >
          <div className={`${styles.avatar} ${styles.avatarCount}`}>
            +{remainingCount}
          </div>
        </div>
      )}
    </div>
  );
}

export default AvatarStack;
